import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { pageHref } from '@/lib/site/i18n';
import type { PageListContext } from '@/lib/widgets/registry';
import type { ListFilter } from '@/types/formProperties';

// ============================================================================
// lib/widgets/sitePageRecords.tsx — записи СТРАНИЦЫ-СПИСКА объекта (хвосты
// Этапов 2–3): рендер серверного постраничного результата (ctx.pageList,
// SSoT — GetPublicPage.records) + пагинация + детерминированная навигация на
// item-страницу (/{slug_prefix}/{ref}; URL-шаблон блока — props.itemUrl).
// Переиспользуется блоком `list` (props.pageRecords) — второй рендер не плодим.
// ============================================================================

/** SiteRecordValues — значения записи (колонки + {col}_presentation, сервер). */
export type SiteRecordValues = Record<string, string>;

/** siteQueryFilters — отборы из query-параметров: ТОЛЬКО поля whitelist
 *  (CSV column_name из настроек страницы/блока); оператор '='; служебные
 *  параметры (page/preview/…) и неизвестные поля игнорируются детерминированно. */
export function siteQueryFilters(search: URLSearchParams, whitelistCsv: string): ListFilter[] {
  const allowed = whitelistCsv.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (allowed.length === 0) return [];
  const reserved = new Set(['page', 'page_size', 'preview', 'preview_token']);
  const out: ListFilter[] = [];
  const seen = new Set<string>();
  search.forEach((value, key) => {
    const field = key.trim().toLowerCase();
    if (!field || reserved.has(field) || seen.has(field)) return;
    if (!allowed.includes(field)) return;
    const v = String(value).trim();
    if (v === '') return;
    seen.add(field);
    out.push({ field, condition: '=', value: v });
  });
  return out;
}

/** siteRecordHref — URL записи списка: шаблон блока ({ref}/{site}/{locale}/
 *  {slug_prefix}) ИЛИ канонический /p/{site}[/{locale}]/{slug_prefix}/{ref}. */
export function siteRecordHref(
  template: string,
  list: PageListContext,
  ref: string,
  siteCode: string,
  locale: string,
  defaultLocale: string,
): string {
  const tmpl = String(template || '').trim();
  if (tmpl) {
    return tmpl
      .replace(/\{ref\}/g, ref)
      .replace(/\{site\}/g, siteCode)
      .replace(/\{locale\}/g, locale)
      .replace(/\{slug_prefix\}/g, list.slugPrefix);
  }
  return pageHref(siteCode, locale, defaultLocale, `${list.slugPrefix}/${ref}`);
}

/** SitePageRecords — записи страницы-списка (карточки/строки) + пагинация. */
export function SitePageRecords({
  list, itemUrlTemplate, viewMode, siteCode, locale, defaultLocale, title,
}: {
  list: PageListContext;
  itemUrlTemplate: string;
  viewMode: string;
  siteCode: string;
  locale: string;
  defaultLocale: string;
  title?: string;
}) {
  const totalPages = Math.max(1, Math.ceil(list.total / Math.max(1, list.pageSize)));
  const cards = viewMode === 'cards';
  return (
    <div className="flex flex-col gap-3 p-2">
      {list.items.length === 0 && (
        <div className="text-sm text-[var(--site-text)] opacity-70 py-4 text-center">Записей нет</div>
      )}
      {cards ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {list.items.map((it) => (
            <RecordLink key={it.ref} href={siteRecordHref(itemUrlTemplate, list, it.ref, siteCode, locale, defaultLocale)}
              className="rounded-lg border border-[var(--site-border)] p-3 hover:shadow-md transition-shadow">
              <RecordText values={it.values} refVal={it.ref} />
            </RecordLink>
          ))}
        </div>
      ) : (
        <div className="flex flex-col divide-y divide-[var(--site-border)] rounded-lg border border-[var(--site-border)] overflow-hidden">
          {list.items.map((it) => (
            <RecordLink key={it.ref} href={siteRecordHref(itemUrlTemplate, list, it.ref, siteCode, locale, defaultLocale)}
              className="px-3 py-2 hover:bg-[var(--site-primary)]/5">
              <RecordText values={it.values} refVal={it.ref} />
            </RecordLink>
          ))}
        </div>
      )}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <PageLink disabled={list.page <= 1} href={list.pageHref(list.page - 1)} title="Предыдущая">
            <ChevronLeft className="h-4 w-4" />
          </PageLink>
          <span className="text-[var(--site-text)] opacity-80">
            {title ? `${title}: ` : ''}{list.page} / {totalPages}
          </span>
          <PageLink disabled={list.page >= totalPages} href={list.pageHref(list.page + 1)} title="Следующая">
            <ChevronRight className="h-4 w-4" />
          </PageLink>
        </div>
      )}
    </div>
  );
}

function RecordText({ values, refVal }: { values: SiteRecordValues; refVal: string }) {
  const title = values.name || values.number || values.code || refVal;
  const description = values.description || '';
  const status = values['status_name'] || values['status_code'] || '';
  const code = values.code && values.code !== title ? values.code : '';
  return (
    <>
      <div className="font-medium text-[var(--site-text)] truncate">{title}</div>
      {(code || status) && (
        <div className="text-xs text-[var(--site-text)] opacity-70 truncate">
          {code}{code && status ? ' · ' : ''}
          {status}
        </div>
      )}
      {description && (
        <div className="text-xs text-[var(--site-text)] opacity-70 line-clamp-2 mt-1">{description}</div>
      )}
    </>
  );
}

function RecordLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  if (/^https?:\/\//i.test(href)) {
    return <a href={href} target="_blank" rel="noopener" className={className}>{children}</a>;
  }
  return <Link to={href} className={className}>{children}</Link>;
}

function PageLink({ href, disabled, title, children }: { href: string; disabled: boolean; title: string; children: ReactNode }) {
  const cls = `inline-flex items-center justify-center h-8 w-8 rounded border border-[var(--site-border)] ${disabled ? 'opacity-40 pointer-events-none' : 'hover:bg-[var(--site-primary)]/10'}`;
  return (
    <Link to={href} className={cls} title={title} aria-disabled={disabled}>
      {children}
    </Link>
  );
}
